import { useState, useEffect } from 'react';
import { Row, Col, Container, Button, Alert, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import InviteUserInformation from '../../components/InviteUser/InviteUserInformation';
import InviteUserPermissions from '../../components/InviteUser/InviteUserPermissions';
import { getCurrentVAccount } from "../../helpers/Session";
import FolderArchitecture from '../../helpers/DocumentPermission';
import MainLayout from '../../layouts/MainLayout';
import UserAnalytic from '../../helpers/UserAnalytic';
import { User } from '../../api';
import EmailValidatedImgURL from '../../assets/img/draw-kit/character_16.png';
import languageManager from '../../helpers/Language';
import { connect } from 'react-redux';
import PermissionManager, { default as PM } from "../../helpers/PermissionManager";
import './InviteUser.scss';

const InviteUser = ({
    profile,
    session
}) => {
    const EMAIL_CONTACT = process.env.REACT_APP_EMAIL_CONTACT;
    const DEFAULT_USER_TYPE = 'regular_user';
    const DEFAULT_USER_INFORMATION = {
        email: '',
        firstname: '',
        lastname: '',
        phone: ''
    };
    const DEFAULT_USER_PORTAL_PERMISSION = {
        drive_access: false,
        drive_upload: false,
        ffda_1099_rw: false,
        ffda_dashboard_rw: false,
        ffda_supplies_rw: false,
        login: false
    };
    const DEFAULT_USER_REPORT_PERMISSION = {
        profitAndLoss: false,
        balanceSheet: false,
        aging: false,
        salesActivity: false
    };
    const DEFAULT_USER_DRIVE_PERMISSION = FolderArchitecture.reduce((accumulator, item) => {
        const codeSplit = item.rcode.split('_');
        const codeFormatted = 'folder_' + codeSplit[codeSplit.length - 2];
        accumulator.push({
            label: item.label,
            code: codeFormatted,
            securityLevel: 0
        });
        return accumulator;
    }, []);

    const [isLoaded, setIsLoaded] = useState(false);
    const [vAccounts, setVAccounts] = useState({});
    const [userAccessType, setUserAccessType] = useState(DEFAULT_USER_TYPE);
    const [userInformation, setUserInformation] = useState({...DEFAULT_USER_INFORMATION})
    const [userPortalPermissions, setUserPortalPermissions] = useState(null);
    const [userDrivePermissions, setUserDrivePermissions] = useState(null);
    const [userReportPermissions, setUserReportPermissions] = useState(null);
    const [formSubmitted, setFormSubmitted] = useState(false);
    const [submittingInvitation, setSubmittingInvitation] = useState(false);
    const [isFormValid, setIsFormValid] = useState(false);
    const [invitationErrors, setInvitationErrors] = useState(null);

    const onAccessTypeChange = (event) => {
        const newAccessType = event.target.value;
        const bool = Boolean(newAccessType === 'business_owner');
        setUserAccessType(newAccessType);
    };

    const onSendInvitation = async (event) => {
        event.preventDefault();
        UserAnalytic.event({
            category: 'settings_user_management',
            action: 'Invite user',
        });

        setSubmittingInvitation(true);

        const data = { 
            emailAddress: userInformation.email,
            firstName: userInformation.firstname,
            lastName: userInformation.lastname,
            phone: userInformation.phone,
            userType: userAccessType,
            isBusinessOwner: userAccessType === 'business_owner',
            portalPermissions: userPortalPermissions,
            drivePermissions: userDrivePermissions
        };
        if (PM.isFederated(session, profile)) {
            data['reportPermissions'] = userReportPermissions;
        }

        try {
            await User.inviteUser(data, (data.userType === 'business_owner'));
            setFormSubmitted(true);
        } catch (error) {
            const errorInfo = error?.response?.data?.detail;
            let errMessage = languageManager.T('SETTINGS_INV_MDL_INV_OOPS');
            if (errorInfo === 'Resource already exists') {
                errMessage = languageManager.T('SETTINGS_INV_MDL_INV_EXISTS');
            }
            setInvitationErrors(errMessage);
        }

        setSubmittingInvitation(false);
    };

    useEffect(() => {
        if (!isLoaded) {
            const vAccountsMap = {};
            const portalPermissionsMap = {};
            const drivePermissionsMap = {};
            const reportPermissionMap = {};

            const currentVAccount = getCurrentVAccount(profile, session);
            // set permissions object for main/parent virtual account
            if (currentVAccount) {
                vAccountsMap[currentVAccount.entityId] = currentVAccount;
                portalPermissionsMap[currentVAccount.entityId] = {...DEFAULT_USER_PORTAL_PERMISSION};
                // set main VAccount login access to true by default
                portalPermissionsMap[currentVAccount.entityId]['login'] = true;
                drivePermissionsMap[currentVAccount.entityId] = [...DEFAULT_USER_DRIVE_PERMISSION];
                reportPermissionMap[currentVAccount.entityId] = {...DEFAULT_USER_REPORT_PERMISSION};

                // set permissions object for each child/chapel
                for (let child of currentVAccount.children) {
                    vAccountsMap[child.entityId] = child;
                    portalPermissionsMap[child.entityId] = {...DEFAULT_USER_PORTAL_PERMISSION};
                    // need to clone objects in drive default permissions to avoid sharing references in between items.
                    drivePermissionsMap[child.entityId] = DEFAULT_USER_DRIVE_PERMISSION.reduce((accumulator, item) => {
                        accumulator.push(new Object({
                            label: item.label,
                            code: item.code,
                            securityLevel: item.securityLevel
                        }))

                        return accumulator;
                    }, []);
                    reportPermissionMap[child.entityId] = {...DEFAULT_USER_REPORT_PERMISSION}
                }
            }

            setVAccounts(vAccountsMap);
            setUserPortalPermissions(portalPermissionsMap);
            setUserDrivePermissions(drivePermissionsMap);
            setUserReportPermissions(reportPermissionMap);
            setIsLoaded(true);
        }
    }, [isLoaded, vAccounts]);

    return (
        <MainLayout pageTitle={languageManager.T('SETTINGS_PAGE_SETTINGS_ACCESS_MANAGEMENT')}>
            <Container className="settings-module">
                <>
                    <Row className="mt-4">
                        <Col lg={12}>
                            <Link to='/settings/access-management'>
                                <Button>{languageManager.T('GEN_BACK')}</Button>
                            </Link>
                        </Col>
                    </Row>
                </>
                {!isLoaded && <h1>loading ...</h1>}
                {(isLoaded && formSubmitted) && (
                    <Row>
                        <Col xs={12} className="text-center">
                            <h1>{languageManager.T('SETTINGS_INV_MDL_INV_SENT')}</h1>
                            <img className="mt-4" src={EmailValidatedImgURL} height={256} />
                        </Col>
                    </Row>
                )}
                {(isLoaded && !formSubmitted) && (
                    <>
                        <Alert 
                            key={'error'} 
                            variant={'danger'} 
                            show={Boolean(invitationErrors)} 
                            className={"mt-3 mb-3 text-center"}
                            onClose={() => setInvitationErrors(null)}
                            dismissible
                        >
                            {invitationErrors}
                        </Alert>
                        <Alert key={'info1'} variant={'info'} show={userAccessType === 'business_owner'} className={"mt-3 mb-3 text-center"}>
                            {languageManager.T('ALERT_CONTACT_EMPLOYEE')}<br />
                            Contact: <strong>{EMAIL_CONTACT}</strong>
                        </Alert>
                        <Row className='mt-3'>
                            <Col lg={6} className='mb-2'>
                                <InviteUserInformation 
                                    userInformation={userInformation}
                                    setUserAccessType={setUserAccessType}
                                    setUserInformation={setUserInformation}
                                    userAccessType={userAccessType}
                                    onAccessTypeChange={onAccessTypeChange}
                                    setIsFormValid={setIsFormValid}
                                />
                            </Col>
                            <Col lg={6}>
                                <div className='mb-4 p-2 rounded bg-white shadow-sm'>
                                    <span className='fw-bold d-block text-center mb-2'>Please assign the desired permissions</span>
                                    Click on "set permissions" to select the permissions and reports that the user will have access to.
                                </div>
                                <InviteUserPermissions 
                                    vAccounts={vAccounts}
                                    portalPermissions={userPortalPermissions}
                                    drivePermissions={userDrivePermissions}
                                    reportPermissions={userReportPermissions}
                                    setPortalPermissions={setUserPortalPermissions}
                                    setDrivePermissions={setUserDrivePermissions}
                                    setReportPermissions={(...args) => {
                                        console.log('setUserReportPermissions:', ...args);
                                        setUserReportPermissions(...args);
                                    }}
                                    userAccessType={userAccessType}
                                    profile={profile}
                                    session={session}
                                />
                            </Col>
                        </Row>
                        <Row className="mt-3">
                            <Col lg={12}>
                                <Button 
                                    disabled={!isFormValid || Boolean(invitationErrors) || submittingInvitation}
                                    onClick={onSendInvitation}
                                >
                                    {submittingInvitation ?
                                        (
                                            <>
                                                <Spinner
                                                    as="span"
                                                    animation="border"
                                                    size="sm"
                                                    role="status"
                                                    aria-hidden="true"
                                                    className='me-2'
                                                />
                                                {languageManager.T('SETTINGS_INV_MDL_BTN_SENDING')}
                                            </>
                                        ) : 
                                        (
                                            <>{languageManager.T('SETTINGS_INV_MDL_BTN_SEND')}</>
                                        )

                                    }
                                </Button>
                            </Col>
                        </Row>
                    </>
                )}
            </Container>
        </MainLayout>
    )
};

const mapStateToProps = (appState) => ({
    session: appState.session,
    profile: appState.userProfile
});

export default connect(mapStateToProps)(InviteUser);
